import { Box, Typography, TextField, Button } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import DashboardLayout from "layout";
import StatsCards from "./StatCards";
import SalesBreakdown from "./SalesBreakdown";
import RevenueTrends from "./RevenueCharts";
import BookingRevenueTrends from "./BookingAndRevenueTrend";
import CustomerInsights from "./CustomersInsight";
import CustomersTable from "./CustomersTable";
import TopPerformingServices from "./PerformingServices";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { getAnalyticsData } from "services/analytics";
import { LoadingState } from "components/LoadingState";

const AnalyticsPage = () => {
  const today = new Date();
  const lastMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    today.getDate(),
  );

  const [fromDate, setFromDate] = useState<Date | null>(lastMonth);
  const [toDate, setToDate] = useState<Date | null>(today);

  const [queryDates, setQueryDates] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: lastMonth,
    to: today,
  });

  const formattedQueryFromDate = queryDates.from
    ? format(queryDates.from, "yyyy-MM-dd")
    : "";
  const formattedQueryToDate = queryDates.to
    ? format(queryDates.to, "yyyy-MM-dd")
    : "";

  const { data: analyticsResponse, isLoading } = useQuery({
    queryKey: ["analytics", formattedQueryFromDate, formattedQueryToDate],
    queryFn: () =>
      getAnalyticsData(formattedQueryFromDate, formattedQueryToDate),
    enabled: !!queryDates.from && !!queryDates.to,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const handleReload = () => {
    setQueryDates({
      from: fromDate,
      to: toDate,
    });
  };

  const analyticsData = analyticsResponse?.data?.stats;

  const stats = analyticsData
    ? [
        {
          id: 1,
          title: "Total Flight Sales",
          value: `₦${Number(analyticsData.totalFlightSales || 0).toLocaleString()}`,
          subtitle: "Across all completed bookings",
          trend: 10,
          trendType: "up",
        },
        {
          id: 2,
          title: "Total Ride Sales",
          value: `₦${Number(analyticsData.totalRideSales || 0).toLocaleString()}`,
          subtitle: "Across all completed bookings",
          trend: 10,
          trendType: "up",
        },
        {
          id: 3,
          title: "Total Flight Trips",
          value: Number(analyticsData.totalFlightTrip || 0).toLocaleString(),
          subtitle: "Total number of flights booked",
          trend: 10,
          trendType: "up",
        },
        {
          id: 4,
          title: "Total Flight Customers",
          value: Number(
            analyticsData?.totalFlightCustomers || 0,
          ).toLocaleString(),
          subtitle: "Total number of customers booked",
          trend: 10,
          trendType: "up",
        },
        {
          id: 5,
          title: "Total Ride Trips",
          value: Number(analyticsData.totalRideTrip || 0).toLocaleString(),
          subtitle: "Total number of rides booked",
          trend: 10,
          trendType: "down",
        },
      ]
    : undefined;

  return (
    <DashboardLayout nextSideBar>
      {isLoading && <LoadingState />}
      <Box sx={{ width: "100%" }}>
        <Box
          mb="36px"
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap="16px"
        >
          <div>
            <Typography color="#101828" fontSize="24px" fontWeight={600}>
              Dashboard
            </Typography>
            <Typography color="#475467" fontSize="16px" fontWeight={400}>
              Track our travel business performance and insights
            </Typography>
          </div>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap={{ xs: "wrap", sm: "nowrap" }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                width={{ xs: "100%", sm: "auto" }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#475467",
                  }}
                >
                  From:
                </Typography>
                <DatePicker
                  value={fromDate}
                  onChange={(date) => setFromDate(date)}
                  slots={{
                    textField: TextField,
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        width: { xs: "100%", sm: "auto" },
                        minWidth: 180,
                        backgroundColor: "white",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#101828",
                          "& fieldset": {
                            borderColor: "#E4E7EC",
                          },
                          "&:hover fieldset": {
                            borderColor: "#379E66",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#379E66",
                          },
                          "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                            color: "#475467",
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>

              <Box
                display="flex"
                alignItems="center"
                gap={1}
                width={{ xs: "100%", sm: "auto" }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#475467",
                  }}
                >
                  To:
                </Typography>
                <DatePicker
                  value={toDate}
                  onChange={(date) => setToDate(date)}
                  minDate={fromDate || undefined}
                  slots={{
                    textField: TextField,
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        width: { xs: "100%", sm: "auto" },
                        minWidth: 180,
                        backgroundColor: "white",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#101828",
                          "& fieldset": {
                            borderColor: "#E4E7EC",
                          },
                          "&:hover fieldset": {
                            borderColor: "#379E66",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#379E66",
                          },
                          "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                            color: "#475467",
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>

              <Button
                variant="contained"
                onClick={handleReload}
                sx={{
                  bgcolor: "#0b1c12",
                  "&:hover": { bgcolor: "#08150e" },
                  height: "40px",
                }}
              >
                Reload
              </Button>
            </Box>
          </LocalizationProvider>
        </Box>

        <StatsCards stats={stats as any} />
        <SalesBreakdown
          data={
            analyticsData
              ? {
                  flight: {
                    months: analyticsData.flightSales.months.reduce<
                      Record<string, number>
                    >(
                      (acc, curr) => ({ ...acc, [curr.month]: curr.value }),
                      {},
                    ),
                    days: analyticsData.flightSales.days.reduce<
                      Record<string, number>
                    >((acc, curr) => ({ ...acc, [curr.day]: curr.value }), {}),
                  },
                  ride: {
                    months: analyticsData.rideSales.months.reduce<
                      Record<string, number>
                    >(
                      (acc, curr) => ({ ...acc, [curr.month]: curr.value }),
                      {},
                    ),
                    days: analyticsData.rideSales.days.reduce<
                      Record<string, number>
                    >((acc, curr) => ({ ...acc, [curr.day]: curr.value }), {}),
                  },
                }
              : undefined
          }
        />
        {analyticsData?.revenueTrend &&
          analyticsData?.revenueTrend.length > 0 && (
            <RevenueTrends revenueTrend={analyticsData?.revenueTrend} />
          )}
        <BookingRevenueTrends
          data={analyticsData?.bookingTrend?.monthlyBreakdown}
          percentages={analyticsData?.bookingTrend?.percentages}
        />
        <CustomerInsights data={analyticsData?.customerBreakdown} />
        <CustomersTable />
        <TopPerformingServices
          airlines={analyticsData?.topAirlines}
          flightDestinations={analyticsData?.topFlightDestinations}
          rideDestinations={analyticsData?.topRideDestinations}
        />
      </Box>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
